export type LuMiMessageStage = 'troubleshooting' | 'done' | 'classification';

export interface LuMiConversationMessage {
  order: number;
  question: string | null;
  answer: string | null;
  options: string[] | null;
  stage: LuMiMessageStage;
  timeToRespond: number;
  isMultimodalQuestion: boolean;
}

export interface LuMiConversationImage {
  id: string;
  summaryId?: string;
  name: string;
  filename: string;
  path: string;
  url: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
}

export interface LuMiConversationSummary {
  summaryId: string;
  summary: string;
  resolved: boolean;
  category: string | null;
  categoryTroubleshootingHistory: string[] | null;
  reasoningsDuringTroubleshooting: string[] | null;
  isEmergency: boolean;
  canBeTroubleshootedBy: string | null;
}

export interface LuMiConversationMetadata {
  totalMessages: number;
  totalSummaries: number;
  totalIssues: number;
  totalImages: number;
  lastActivityAt: string | null;
  submittedAt: string | null;
  status: string;
  isEmergency: boolean;
  priority: string | null;
  category: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LuMiConversationTranscript {
  serviceRequestId: string;
  sessionId: string;
  initialQuestion: string | null;
  description: string | null;
  messages: LuMiConversationMessage[];
  summaries: LuMiConversationSummary[];
  images: LuMiConversationImage[];
  metadata: LuMiConversationMetadata;
}

export interface LuMiIntakeTranscriptApiResponse {
  uuid: string;
  service_request_uuid: string;
  fetched_at: string | null;
  snapshot: {
    data: Record<string, unknown>;
    success: boolean;
  };
}

// State maps are keyed by service_request_uuid so multiple SR side-panels can coexist.
export interface LuMiConversationState {
  transcripts: Record<string, LuMiConversationTranscript>;
  loading: Record<string, boolean>;
  errors: Record<string, boolean>;
  /**
   * Transient flag used to coordinate cross-panel navigation: when true, the
   * Activity Log should switch to the "events" tab and auto-expand the LuMi
   * conversation history block. Consumed and cleared after handling.
   */
  pendingTranscriptOpen: boolean;
}

export const initialState: LuMiConversationState = {
  transcripts: {},
  loading: {},
  errors: {},
  pendingTranscriptOpen: false,
};

export const transformLuMiMessage = (
  message: Record<string, unknown>,
): LuMiConversationMessage => ({
  order: (message.order as number) ?? 0,
  question: (message.question as string | null) ?? null,
  answer: (message.answer as string | null) ?? null,
  options: (message.options as string[] | null) ?? null,
  stage: message.stage as LuMiMessageStage,
  timeToRespond: (message.time_to_respond as number) ?? 0,
  isMultimodalQuestion: (message.is_multimodal_question as boolean) ?? false,
});

export const transformLuMiImage = (image: Record<string, unknown>): LuMiConversationImage => ({
  id: image.id as string,
  summaryId: image.summary_id as string | undefined,
  name: image.name as string,
  filename: image.filename as string,
  path: image.path as string,
  url: image.url as string,
  mimeType: image.mime_type as string,
  size: image.size as number,
  uploadedAt: image.uploaded_at as string,
});

export const transformLuMiSummary = (
  summary: Record<string, unknown>,
): LuMiConversationSummary => ({
  summaryId: summary.summary_id as string,
  summary: summary.summary as string,
  resolved: summary.resolved as boolean,
  category: summary.category as string | null,
  categoryTroubleshootingHistory: summary.category_troubleshooting_history as string[] | null,
  reasoningsDuringTroubleshooting: summary.reasonings_during_troubleshooting as string[] | null,
  isEmergency: summary.is_emergency as boolean,
  canBeTroubleshootedBy: summary.can_be_troubleshooted_by as string | null,
});

export const transformLuMiMetadata = (
  metadata: Record<string, unknown> = {},
): LuMiConversationMetadata => ({
  totalMessages: (metadata.total_messages as number) ?? 0,
  totalSummaries: (metadata.total_summaries as number) ?? 0,
  totalIssues: (metadata.total_issues as number) ?? 0,
  totalImages: (metadata.total_images as number) ?? 0,
  lastActivityAt: (metadata.last_activity_at as string | null) ?? null,
  submittedAt: (metadata.submitted_at as string | null) ?? null,
  status: (metadata.status as string) ?? '',
  isEmergency: (metadata.is_emergency as boolean) ?? false,
  priority: (metadata.priority as string | null) ?? null,
  category: (metadata.category as string | null) ?? null,
  createdAt: (metadata.created_at as string) ?? '',
  updatedAt: (metadata.updated_at as string) ?? '',
});

export const transformLuMiSnapshot = (
  snapshot: Record<string, unknown> = {},
  serviceRequestUuid = '',
): LuMiConversationTranscript => {
  const messages = (snapshot.messages as Record<string, unknown>[]) ?? [];
  const summaries = (snapshot.summaries as Record<string, unknown>[]) ?? [];
  const images = (snapshot.images as Record<string, unknown>[]) ?? [];

  return {
    serviceRequestId: serviceRequestUuid,
    sessionId: (snapshot.session_id as string) ?? '',
    initialQuestion: (snapshot.initial_question as string | null) ?? null,
    description: (snapshot.description as string | null) ?? null,
    messages: messages.map(transformLuMiMessage),
    summaries: summaries.map(transformLuMiSummary),
    images: images.map(transformLuMiImage),
    metadata: transformLuMiMetadata((snapshot.metadata as Record<string, unknown>) ?? {}),
  };
};
